import { useState } from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { HarnessCoachPrepSuggestion } from "@/types/harnessPayload";

interface CoachHarnessPrepBannerProps {
  suggestions: HarnessCoachPrepSuggestion[];
  onDismiss?: () => void;
}

export function CoachHarnessPrepBanner({ suggestions, onDismiss }: CoachHarnessPrepBannerProps) {
  const [collapsed, setCollapsed] = useState(false);

  if (suggestions.length === 0) return null;

  const visibleSuggestions = suggestions.slice(0, 3);

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/5 px-3 py-2">
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-1.5 text-xs font-medium text-amber-800 dark:text-amber-300"
          onClick={() => setCollapsed((prev) => !prev)}
        >
          {collapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
          Prep suggestions ({suggestions.length})
        </button>
        {onDismiss && (
          <button
            type="button"
            className="rounded p-0.5 text-amber-600 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-500/10"
            onClick={onDismiss}
            aria-label="Dismiss prep suggestions"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {!collapsed && (
        <div className="mt-2 space-y-1.5">
          {visibleSuggestions.map((suggestion) => (
            <div
              key={suggestion.candidateId}
              className="flex items-start gap-2 rounded-md border border-amber-200/60 bg-white/60 dark:border-amber-500/20 dark:bg-background/40 px-2.5 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                    {suggestion.frameworkPackLabel}
                  </span>
                  <Badge variant="outline" className="h-4 px-1.5 text-[10px]">
                    {suggestion.evidenceCount} evidence
                  </Badge>
                </div>
                <p className="mt-0.5 text-xs text-foreground/80 truncate">
                  {suggestion.momentSummary.length > 80
                    ? `${suggestion.momentSummary.slice(0, 80)}...`
                    : suggestion.momentSummary}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Focus: {suggestion.suggestedFocus}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
